import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, ArrowRight, Clock, Share2, Linkedin, Link2, Check } from "lucide-react";
import Seo from "../components/Seo";
import { openSubscribeGate } from "../lib/subscription";
import { toast } from "sonner";
import { BlogSubscribeBox } from "../components/BlogSubscribeBox";

const API = process.env.REACT_APP_BACKEND_URL || "";

const MD = {
  h2: (p) => <h2 className="font-display text-xl font-semibold text-white mt-8 mb-3" {...p} />,
  h3: (p) => <h3 className="text-base font-semibold text-white mt-6 mb-2" {...p} />,
  p: (p) => <p className="text-sm text-zinc-300 leading-relaxed mb-4" {...p} />,
  ul: (p) => <ul className="list-disc pl-5 mb-4 space-y-1.5 text-sm text-zinc-300" {...p} />,
  ol: (p) => <ol className="list-decimal pl-5 mb-4 space-y-1.5 text-sm text-zinc-300" {...p} />,
  li: (p) => <li className="leading-relaxed" {...p} />,
  strong: (p) => <strong className="text-white font-semibold" {...p} />,
  a: (p) => <a className="text-[#39FF14] hover:underline" {...p} />,
  blockquote: (p) => (
    <blockquote className="border-l-2 border-[#39FF14]/50 pl-4 text-zinc-400 italic mb-4" {...p} />
  ),
  table: (p) => (
    <div className="overflow-x-auto mb-4 rounded-xl border border-white/10">
      <table className="w-full text-sm" {...p} />
    </div>
  ),
  thead: (p) => <thead className="border-b border-white/10" {...p} />,
  th: (p) => (
    <th className="px-3 py-2 text-left font-mono text-[10px] uppercase tracking-wider text-zinc-500 font-medium" {...p} />
  ),
  td: (p) => <td className="px-3 py-2 text-[13px] text-zinc-300 border-b border-white/5" {...p} />,
};

const ShareRow = ({ post }) => {
  const [copied, setCopied] = useState(false);
  const shareUrl = `https://eviq.pro/api/blog/${post.slug}/share`;
  const canonical = `https://eviq.pro/blog/${post.slug}`;
  const enc = encodeURIComponent;
  const btn =
    "inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-zinc-400 hover:text-white hover:border-[#39FF14]/50 transition-colors";
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(canonical);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — long-press the address bar instead");
    }
  };
  return (
    <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="post-share-row">
      <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-zinc-600">
        <Share2 className="w-3 h-3" /> Share
      </span>
      <a
        href={`https://twitter.com/intent/tweet?text=${enc(post.title)}&url=${enc(shareUrl)}`}
        target="_blank"
        rel="noopener noreferrer"
        className={btn}
        data-testid="post-share-x"
      >
        <svg viewBox="0 0 24 24" className="w-3 h-3 fill-current" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
        X
      </a>
      <a
        href={`https://www.linkedin.com/sharing/share-offsite/?url=${enc(shareUrl)}`}
        target="_blank"
        rel="noopener noreferrer"
        className={btn}
        data-testid="post-share-linkedin"
      >
        <Linkedin className="w-3 h-3" /> LinkedIn
      </a>
      <button type="button" onClick={copy} className={btn} data-testid="post-share-copy">
        {copied ? <Check className="w-3 h-3 text-[#39FF14]" /> : <Link2 className="w-3 h-3" />}
        {copied ? "Copied" : "Copy link"}
      </button>
    </div>
  );
};

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(undefined);

  useEffect(() => {
    setPost(undefined);
    fetch(`${API}/api/blog/${slug}`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setPost)
      .catch(() => setPost(null));
    window.scrollTo(0, 0);
  }, [slug]);

  if (post === undefined) {
    return <div className="min-h-screen bg-black text-white flex items-center justify-center font-mono text-xs text-zinc-500">Loading article…</div>;
  }
  if (post === null) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6">
        <div className="text-center">
          <div className="text-sm text-zinc-300 mb-3">Article not found.</div>
          <Link to="/blog" className="text-[11px] font-mono uppercase tracking-wider text-[#39FF14]">← All articles</Link>
        </div>
      </div>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    datePublished: post.published_at,
    author: { "@type": "Organization", name: "EVIQ" },
    publisher: { "@type": "Organization", name: "EVIQ", url: "https://eviq.pro" },
    mainEntityOfPage: `https://eviq.pro/blog/${post.slug}`,
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="blog-post-page">
      <Seo
        title={post.title}
        description={post.excerpt || post.title}
        canonical={`https://eviq.pro/blog/${post.slug}`}
        ogImage={`https://eviq.pro/api/blog/${post.slug}/og.png`}
        jsonLd={jsonLd}
      />
      <article className="max-w-2xl mx-auto px-5 py-10">
        <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors" data-testid="post-back-blog">
          <ArrowLeft className="w-4 h-4" /> All articles
        </Link>

        <div className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[10px] uppercase tracking-wider text-zinc-500">
          <span>
            {new Date(post.published_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}
          </span>
          <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {post.reading_min} min read</span>
          {(post.tags || []).map((t) => (
            <span key={t} className="rounded-full border border-white/10 px-2 py-0.5 text-[#39FF14]">{t}</span>
          ))}
        </div>
        <h1 className="font-display mt-3 text-3xl sm:text-4xl font-semibold leading-tight" data-testid="post-title">
          {post.title}
        </h1>

        <div className="mt-8" data-testid="post-body">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD}>{post.body_md}</ReactMarkdown>
        </div>

        <ShareRow post={post} />

        <div className="mt-10">
          <BlogSubscribeBox />
        </div>

        <div className="mt-12 rounded-2xl border border-white/10 bg-white/[0.02] p-6 text-center">
          <div className="text-sm text-white font-medium">Want these numbers for your own pack?</div>
          <p className="mt-1 text-xs text-zinc-500">7-day free preview · Card required · Cancel by email in one line</p>
          <button
            type="button"
            onClick={() => openSubscribeGate("blog-post")}
            data-testid="post-cta"
            className="mt-4 brand-gradient text-black font-semibold rounded-md px-6 py-2.5 text-sm inline-flex items-center gap-2 hover:opacity-90 transition-opacity"
          >
            Start 7-day free preview <ArrowRight className="w-4 h-4" strokeWidth={2.4} />
          </button>
        </div>
      </article>
    </div>
  );
}
